// Keeps the user's settings (theme, language, sounds, notifications, muted servers, folders, ...) in the database,
// so they follow the account to every device. localStorage stays as the fast local copy.
type Request = (url: string, method?: string, body?: unknown) => Promise<any>;
// Values that belong to this device/browser only.
const DEVICE_ONLY = new Set(['malvo-pip-pos','malvo-call-dock-height','malvo-audioinput', 'malvo-audiooutput', 'malvo-notif-asked', 'malvo-games-position', 'malvo-clips-microphone', 'malvo-dm-profile','malvo-hide-own-stream','malvo-settings-account']);
const isSynced = (key: string) => (key.startsWith('malvo-') || key === 'hide_win_banner') && !DEVICE_ONLY.has(key) && !key.startsWith('malvo-local-settings:') && !key.startsWith('malvo-room-drafts:') && !key.startsWith('malvo-chat-positions:') && !key.endsWith('-position');

let pending: Record<string, string | null> = {};
let timer: ReturnType<typeof setTimeout> | null = null;
let request: Request | null = null;
let patched = false;
let activeAccount:string|null=null;
let writing: {owner:string|null;body:Record<string,string|null>}|null=null;
let applyingRemote=false,readRevision=0,retryDelay=1000;

function schedule(delay=800){if(!timer&&request&&Object.keys(pending).length)timer=setTimeout(flush,delay);}

async function flush() {
  timer = null;
  if (writing || !request || !Object.keys(pending).length) return;
  const body = pending; pending = {};
  const task={owner:activeAccount,body};writing=task;
  try{await request('/settings','PUT',body);retryDelay=1000;if(task.owner===activeAccount)window.dispatchEvent(new CustomEvent('malvo-settings-sync',{detail:{keys:Object.keys(body),failed:false}}));}
  catch{if(task.owner===activeAccount){pending={...body,...pending};retryDelay=Math.min(retryDelay*2,15000);window.dispatchEvent(new CustomEvent('malvo-settings-sync',{detail:{keys:Object.keys(body),failed:true}}));}}
  finally{if(writing===task)writing=null;schedule(retryDelay);}
}
function queue(key: string, value: string | null) {
  if (!request || applyingRemote || !isSynced(key)) return;
  pending[key] = value;
  schedule();
}

export async function startSettingsSync(api: Request,accountId?:string) {
  if(accountId&&activeAccount!==accountId){
    request=null;pending={};writing=null;readRevision++;if(timer)clearTimeout(timer);timer=null;
    const previous=localStorage.getItem('malvo-settings-account');
    if(previous&&previous!==accountId){
      const snapshot:Record<string,string>={};for(let i=0;i<localStorage.length;i++){const key=localStorage.key(i)!;if(isSynced(key))snapshot[key]=localStorage.getItem(key)!;}
      try{localStorage.setItem('malvo-local-settings:'+previous,JSON.stringify(snapshot))}catch{}
      for(const key of Object.keys(snapshot))localStorage.removeItem(key);
      try{const stored=JSON.parse(localStorage.getItem('malvo-local-settings:'+accountId)||'{}');for(const [key,value] of Object.entries(stored))if(isSynced(key)&&typeof value==='string')localStorage.setItem(key,value)}catch{}
    }
    activeAccount=accountId;localStorage.setItem('malvo-settings-account',accountId);
    const legacy=localStorage.getItem('malvo-chat-open-position');if(legacy&&!localStorage.getItem('malvo-chat-open-mode'))localStorage.setItem('malvo-chat-open-mode',legacy);
  }
  const owner=activeAccount;
  const revision=++readRevision;
  const protectedKeys=new Set([...Object.keys(pending),...(writing?.owner===owner?Object.keys(writing.body):[])]);
  // A GET started before a local change must never undo it, even after its PUT finishes.
  const localBefore=new Map<string,string|null>();for(let i=0;i<localStorage.length;i++){const key=localStorage.key(i)!;if(isSynced(key))localBefore.set(key,localStorage.getItem(key));}
  request = api;
  if (!patched) {
    patched = true;
    const set = Storage.prototype.setItem, remove = Storage.prototype.removeItem;
    Storage.prototype.setItem = function (key: string, value: string) {
      const changed = this === localStorage && this.getItem(key) !== String(value);
      set.call(this, key, value);
      if (changed) queue(key, String(value));
    };
    Storage.prototype.removeItem = function (key: string) {
      const had = this === localStorage && this.getItem(key) !== null;
      remove.call(this, key);
      if (had) queue(key, null);
    };
  }
  const remote: Record<string, string> = await api('/settings');
  if(owner!==activeAccount||revision!==readRevision)return false;
  const set = Storage.prototype.setItem;
  let changed = false;
  if (!Object.keys(remote).length) {
    // First time: upload what this browser already has.
    for (let i = 0; i < localStorage.length; i++) { const k = localStorage.key(i)!; if (isSynced(k)) pending[k] = localStorage.getItem(k); }
    if (Object.keys(pending).length) void flush();
    return false;
  }
  for (const [k, v] of Object.entries(remote)) {
    if (!isSynced(k) || localStorage.getItem(k) === v || protectedKeys.has(k) || Object.hasOwn(pending,k) || writing?.owner===owner&&Object.hasOwn(writing.body,k) || localStorage.getItem(k)!==(localBefore.get(k)??null)) continue;
    applyingRemote=true;
    try { set.call(localStorage, k, v); changed = true; } catch {}finally{applyingRemote=false;}
  }
  if(changed)window.dispatchEvent(new Event('malvo-settings-updated'));
  return changed;
}
